import { WebMercatorViewport } from "@math.gl/web-mercator";
import { Box } from "@mui/material";
import {
  getFeatureCollectionBbox,
  isValidLngLatBbox,
} from "common/utils/mapGeometry";
import { transformMapTileRequest } from "common/utils/mapTransformRequest";
import { ONLINE_MAP_STYLE } from "features/mapLayers/offlineMapStyle";
import type { FeatureCollection, Geometry } from "geojson";
import type { MapLayerMouseEvent } from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import { Layer, Map, MapRef, Source } from "react-map-gl/mapbox";

type SBIMapField = {
  sourceIndex: number;
  name: string;
  include: boolean;
  geometry: Geometry;
};

type ViewportState = {
  latitude: number;
  longitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
};

const initialViewport: ViewportState = {
  latitude: 52.794201,
  longitude: -1.1945036,
  zoom: 12,
  bearing: 0,
  pitch: 0,
};

interface SBIImportMapPreviewProps {
  activeSourceIndex?: number;
  fields: SBIMapField[];
  onFieldClick?: (sourceIndex: number) => void;
}

function getViewportForFields(
  geojson: FeatureCollection,
  width: number,
  height: number
): ViewportState {
  const bounds = getFeatureCollectionBbox(geojson);

  if (!bounds || !isValidLngLatBbox(bounds) || width <= 0 || height <= 0) {
    return initialViewport;
  }

  const [minLng, minLat, maxLng, maxLat] = bounds;

  try {
    const viewport = new WebMercatorViewport({
      ...initialViewport,
      width,
      height,
    });
    const fitted = viewport.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 28 }
    );

    return {
      ...initialViewport,
      longitude: fitted.longitude,
      latitude: fitted.latitude,
      zoom: fitted.zoom - 0.25,
    };
  } catch {
    return initialViewport;
  }
}

export default function SBIImportMapPreview({
  activeSourceIndex,
  fields,
  onFieldClick,
}: SBIImportMapPreviewProps) {
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<ViewportState>(initialViewport);

  const geojson = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: fields.map((field) => ({
        type: "Feature",
        properties: {
          include: field.include,
          isActive: field.sourceIndex === activeSourceIndex,
          name: field.name,
          sourceIndex: field.sourceIndex,
        },
        geometry: field.geometry,
      })),
    }),
    [activeSourceIndex, fields]
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const { width, height } = containerRef.current.getBoundingClientRect();
    setViewport(getViewportForFields(geojson, width, height));
  }, [geojson]);

  const handleMapClick = (event: MapLayerMouseEvent) => {
    const sourceIndex = Number(event.features?.[0]?.properties?.sourceIndex);

    if (Number.isFinite(sourceIndex)) {
      onFieldClick?.(sourceIndex);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        borderRadius: 2,
        height: { xs: 220, md: "100%" },
        minHeight: 220,
        overflow: "hidden",
        position: "relative",
        width: "100%",
        "& .mapboxgl-ctrl-attrib": { display: "none" },
        "& .mapboxgl-ctrl-logo": { display: "none" },
      }}
    >
      <Map
        ref={mapRef}
        attributionControl={false}
        bearing={viewport.bearing}
        doubleClickZoom={false}
        dragPan={false}
        dragRotate={false}
        interactiveLayerIds={["sbi-import-fill"]}
        keyboard={false}
        latitude={viewport.latitude}
        longitude={viewport.longitude}
        mapStyle={ONLINE_MAP_STYLE}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        maxPitch={0}
        pitch={viewport.pitch}
        pitchWithRotate={false}
        scrollZoom={false}
        touchZoomRotate={false}
        transformRequest={transformMapTileRequest}
        onClick={handleMapClick}
        zoom={viewport.zoom}
      >
        <Source data={geojson} id="sbi-import-preview" type="geojson">
          <Layer
            id="sbi-import-fill"
            paint={{
              "fill-color": [
                "case",
                ["==", ["get", "isActive"], true],
                "#f6c343",
                ["==", ["get", "include"], true],
                "#2e7d32",
                "#6b7280",
              ],
              "fill-opacity": [
                "case",
                ["==", ["get", "isActive"], true],
                0.55,
                ["==", ["get", "include"], true],
                0.35,
                0.18,
              ],
            }}
            type="fill"
          />
          <Layer
            id="sbi-import-outline"
            paint={{
              "line-color": [
                "case",
                ["==", ["get", "isActive"], true],
                "#f6c343",
                ["==", ["get", "include"], true],
                "#ffffff",
                "#9ca3af",
              ],
              "line-width": ["case", ["==", ["get", "isActive"], true], 4, 2],
            }}
            type="line"
          />
        </Source>
      </Map>
    </Box>
  );
}
